import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  AffectedItemsResponse,
  InsightFilters,
  RiskInsight,
  RiskInsightList,
  RiskInsightUpdate,
} from './insights-client.type';

const axiosInstance = AxiosInstance();
const insightsBaseURL = appConfig.VITE_APP_API_URL + '/api/risk-insights/';

/**
 * Fetches risk insights based on provided filters
 *
 * @param {InsightFilters} filters - Optional filters for insights
 * @returns {Promise<RiskInsightList[]>} - A promise that resolves to an array of insights
 */
const fetchRiskInsights = (filters?: InsightFilters): Promise<RiskInsightList[]> => {
  const params = new URLSearchParams();

  // Support both single insight_type and multiple insight_types
  if (filters?.insight_types && filters.insight_types.length > 0) {
    // For multiple types, append each one as a separate parameter
    filters.insight_types.forEach((type) => {
      params.append('insight_type', type);
    });
  } else if (filters?.insight_type) {
    params.append('insight_type', filters.insight_type);
  }

  if (filters?.severity) {
    params.append('severity', filters.severity);
  }
  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.is_active !== undefined) {
    params.append('is_active', filters.is_active.toString());
  }
  if (filters?.is_dismissed !== undefined) {
    params.append('is_dismissed', filters.is_dismissed.toString());
  }

  const apiURL = `${insightsBaseURL}${params.toString() ? `?${params.toString()}` : ''}`;

  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching insights'));
};

/**
 * Updates a risk insight
 *
 * @param {string} insightId - The ID of the insight to update
 * @param {RiskInsightUpdate} data - The data to update
 * @returns {Promise<RiskInsight>} - A promise that resolves to the updated insight
 */
const updateRiskInsight = (insightId: string, data: RiskInsightUpdate): Promise<RiskInsight> => {
  return axiosInstance
    .patch(`${insightsBaseURL}${insightId}/`, data)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while updating insight'));
};

/**
 * Dismisses a risk insight
 *
 * @param {string} insightId - The ID of the insight to dismiss
 * @returns {Promise<RiskInsight>} - A promise that resolves to the dismissed insight
 */
const dismissRiskInsight = (insightId: string): Promise<RiskInsight> => {
  return axiosInstance
    .post(`${insightsBaseURL}${insightId}/dismiss/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while dismissing insight'));
};

/**
 * Fetches affected items for a specific insight
 *
 * @param {string} insightId - The ID of the insight
 * @returns {Promise<AffectedItemsResponse>} - A promise that resolves to the affected items
 */
const fetchAffectedItems = (insightId: string): Promise<AffectedItemsResponse> => {
  return axiosInstance
    .get(`${insightsBaseURL}${insightId}/affected_items/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching affected items'));
};

export { dismissRiskInsight, fetchAffectedItems, fetchRiskInsights, updateRiskInsight };
